/** Unde e soarele deasupra parcelei, la o oră dată.
 *
 *  DE CE E CALCULAT, NU DESENAT. Macheta de situație nu arată o clădire, ci
 *  clădirea AICI: o amprentă care trece de POT poate să pice la umbrire, iar
 *  între cele două întrebări nu e nimic în afară de poziția soarelui. Lumina
 *  din machetă e deci aceeași mărime pe care o cere norma, nu o direcție aleasă
 *  ca să arate bine. Cine mută ora mută și verdictul.
 *
 *  Algoritmul e cel al NOAA (Solar Position Calculator) — ecuația timpului,
 *  declinația, unghiul orar. Precizia lui e sub un minut de arc pentru anii din
 *  jurul lui 2000 ± 100, adică cu două ordine de mărime mai mult decât are
 *  nevoie o umbră de bloc.
 *
 *  CONVENȚIILE, fiindcă aici se greșește:
 *   · `az` e azimutul de busolă — 0 la NORD, crescător spre EST.
 *   · `alt` e unghiul deasupra orizontului, negativ sub el.
 *   · ora e LOCALĂ, zecimală (13,5 = 13:30), iar fusul se dă în ore.
 *   · vectorul întors de `vectorSoare` e în CADRUL DESENULUI, nu în cel
 *     geografic — v. nota lui. */

import { laWgs84, inRomania } from "./stereo70";
import type { Pt } from "../types";

export interface PozitieSoare {
  /** Grade deasupra orizontului; negativ înseamnă sub el. */
  alt: number;
  /** Azimut de busolă, în grade: 0 = nord, 90 = est. */
  az: number;
  /** Declinația, în grade — de ea depinde anotimpul. */
  decl: number;
  /** Ecuația timpului, în minute. */
  eqT: number;
}

export interface Loc { lat: number; lon: number }

/** Amplasamentul folosit când documentul nu spune unde e.
 *
 *  Nu e o alegere de decor: fără coordonate reale nu se poate calcula NIMIC,
 *  iar un studiu de însorire pe o parcelă imaginară e mai rău decât lipsa lui.
 *  De asta interfața trebuie să spună când e pe implicit — v. `locSit`, care
 *  întoarce și de unde vine răspunsul. */
export const BUCURESTI: Loc = { lat: 44.4268, lon: 26.1025 };

/** Fusul în care se citește ceasul unei parcele din România. */
export const FUS_RO = "Europe/Bucharest";

const rad = (d: number) => (d * Math.PI) / 180;
const grd = (r: number) => (r * 180) / Math.PI;
const m360 = (x: number) => ((x % 360) + 360) % 360;

/** Ziua juliană la 0h UT pentru o dată din calendarul gregorian. */
export function zileJuliene(an: number, luna: number, zi: number): number {
  let y = an, m = luna;
  if (m <= 2) { y -= 1; m += 12; }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + zi + b - 1524.5;
}

/** Decalajul unui fus la o dată dată, în ore.
 *
 *  Se citește din motorul de fusuri al browserului, nu din reguli scrise de
 *  mână. Regula europeană de oră de vară s-a schimbat o dată și e programată să
 *  se mai schimbe; un `if (luna >= 4 && luna <= 9)` scris aici ar fi corect azi
 *  și greșit la prima modificare, fără să se plângă nimeni — iar efectul ar fi
 *  o oră lipsă exact în studiul de umbrire. */
export function decalajFus(an: number, luna: number, zi: number,
                           ora = 12, fus: string = FUS_RO): number {
  // Două treceri: prima ghicește decalajul tratând ora ca UT, a doua îl
  // recalculează pentru instantul local corect. Contează doar în cele două
  // nopți pe an în care ceasul sare.
  let t = Date.UTC(an, luna - 1, zi, 12, 0);
  let off = 0;
  for (let k = 0; k < 2; k++) {
    off = decalajLaInstant(new Date(t), fus);
    t = Date.UTC(an, luna - 1, zi, 0, 0) + Math.round((ora - off) * 3600000);
  }
  return off;
}

function decalajLaInstant(d: Date, fus: string): number {
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: fus, hour12: false, year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(d)) p[x.type] = x.value;
  const caDacaArFiUtc = Date.UTC(+p.year, +p.month - 1, +p.day, (+p.hour) % 24, +p.minute);
  return Math.round((caDacaArFiUtc - d.getTime()) / 60000) / 60;
}

/** Poziția soarelui. `ora` e locală și zecimală, `fus` în ore. */
export function pozitiaSoarelui(an: number, luna: number, zi: number, ora: number,
                                loc: Loc, fus: number): PozitieSoare {
  const jd = zileJuliene(an, luna, zi) + (ora - fus) / 24;
  const T = (jd - 2451545) / 36525;
  const L0 = m360(280.46646 + T * (36000.76983 + T * 0.0003032));
  const M = 357.52911 + T * (35999.05029 - 0.0001537 * T);
  const Mr = rad(M);
  const e = 0.016708634 - T * (0.000042037 + 0.0000001267 * T);
  const C = Math.sin(Mr) * (1.914602 - T * (0.004817 + 0.000014 * T))
          + Math.sin(2 * Mr) * (0.019993 - 0.000101 * T)
          + Math.sin(3 * Mr) * 0.000289;
  const om = rad(125.04 - 1934.136 * T);
  const lam = rad(L0 + C - 0.00569 - 0.00478 * Math.sin(om));
  const eps = rad(23 + (26 + (21.448 - T * (46.815 + T * (0.00059 - T * 0.001813))) / 60) / 60
                  + 0.00256 * Math.cos(om));
  const decl = Math.asin(Math.sin(eps) * Math.sin(lam));
  const yv = Math.tan(eps / 2) * Math.tan(eps / 2);
  const L0r = rad(L0);
  const eqT = 4 * grd(yv * Math.sin(2 * L0r) - 2 * e * Math.sin(Mr)
                    + 4 * e * yv * Math.sin(Mr) * Math.cos(2 * L0r)
                    - 0.5 * yv * yv * Math.sin(4 * L0r)
                    - 1.25 * e * e * Math.sin(2 * Mr));
  const tst = (ora * 60 + eqT + 4 * loc.lon - 60 * fus + 2880) % 1440;
  const ha = tst / 4 - 180;
  const lr = rad(loc.lat);
  const cz = Math.sin(lr) * Math.sin(decl) + Math.cos(lr) * Math.cos(decl) * Math.cos(rad(ha));
  const z = Math.acos(Math.max(-1, Math.min(1, cz)));
  const alt = 90 - grd(z);
  let az: number;
  const den = Math.cos(lr) * Math.sin(z);
  if (Math.abs(den) > 1e-6) {
    const c = Math.max(-1, Math.min(1, (Math.sin(lr) * Math.cos(z) - Math.sin(decl)) / den));
    az = 180 - grd(Math.acos(c));
    if (ha > 0) az = -az;
  } else {
    az = 180;
  }
  return { alt, az: m360(az), decl: grd(decl), eqT };
}

export interface Efemeride {
  /** Ore locale zecimale. `null` când soarele nu trece orizontul în ziua aia —
   *  nu se întâmplă în România, dar formula e aceeași peste tot. */
  rasarit: number | null;
  apus: number | null;
  /** Amiaza SOLARĂ, adică momentul culminației — nu ora 12. */
  amiaza: number;
}

/** Răsăritul și apusul, cu refracția standard (−0,833°, adică marginea de sus a
 *  discului la orizontul aparent). */
export function efemeride(an: number, luna: number, zi: number,
                          loc: Loc, fus: number): Efemeride {
  const s = pozitiaSoarelui(an, luna, zi, 12, loc, fus);
  const lr = rad(loc.lat), dc = rad(s.decl);
  const amiaza = (720 - 4 * loc.lon - s.eqT) / 60 + fus;
  const ch = (Math.cos(rad(90.833)) - Math.sin(lr) * Math.sin(dc))
           / (Math.cos(lr) * Math.cos(dc));
  if (ch > 1) return { rasarit: null, apus: null, amiaza };     // nu răsare
  if (ch < -1) return { rasarit: 0, apus: 24, amiaza };         // nu apune
  const H = grd(Math.acos(ch)) / 15;
  return { rasarit: amiaza - H, apus: amiaza + H, amiaza };
}

/** Direcția SPRE soare, în cadrul desenului.
 *
 *  AICI E CAPCANA, și merită scrisă o dată pentru totdeauna. Documentul ajunge
 *  în vizualizator trecut prin `canonical.northUp`, care duce fiecare punct în
 *  `[est, −nord]`: în cadrul desenat **+x e estul, +y e SUDUL**, fiindcă y-ul
 *  unei pânze crește în jos și nordul trebuie să iasă în sus pe ecran. Z-ul
 *  rămâne în sus, ca în document.
 *
 *  Deci nordul nu e `+y`, e `−y`. Un vector scris după convenția geografică
 *  obișnuită ar da o machetă în care umbrele cad exact invers — la prânz spre
 *  privitor în loc de spre fundal — și ar trece neobservat la orice oră
 *  simetrică față de sud. */
export function vectorSoare(alt: number, az: number): [number, number, number] {
  const a = rad(alt), z = rad(az);
  return [Math.cos(a) * Math.sin(z), -Math.cos(a) * Math.cos(z), Math.sin(a)];
}

export interface LocSit {
  loc: Loc;
  /** `parcela` — citit din coordonatele documentului; `implicit` — n-au fost
   *  de unde. Interfața spune care din ele e, fiindcă un studiu de însorire pe
   *  coordonate implicite nu înseamnă nimic. */
  sursa: "parcela" | "implicit";
}

/** Unde stă parcela, din coordonatele ei.
 *
 *  `lot` e conturul așa cum îl DESENEAZĂ vizualizatorul, iar asta înseamnă două
 *  transformări, nu una:
 *
 *   1. `northUp` a dus documentul în cadrul desenului — `[est, −nord]`;
 *   2. `laOrigineLocala` a SCĂZUT din tot o origine rotunjită la kilometru,
 *      fiindcă la coordonate Stereo 70 absolute pânza se albea peste 57 px/m.
 *
 *  Deci punctele primite aici sunt mici (zeci de metri) chiar și pe un document
 *  perfect georeferențiat, și trebuie ADUNATĂ originea la loc înainte de
 *  proiecție. Fără ea, centrul lotului cădea la vreo 500 km vest de falsul est,
 *  adică în larg — `inRomania` îl respingea, iar panoul anunța cinstit că n-are
 *  coordonate pe un document care le avea pe toate. Verificarea nu era de vină;
 *  ce i se dădea, da.
 *
 *  Guardul rămâne: un document de import chiar are coordonate locale (originea
 *  lui e `[0, 0]`), iar acolo nu se poate calcula nimic. */
export function locSit(lot: Pt[] | null | undefined,
                       georeferentiat: boolean,
                       origine: readonly [number, number] = [0, 0]): LocSit {
  if (!georeferentiat || !lot || lot.length < 3) {
    return { loc: BUCURESTI, sursa: "implicit" };
  }
  let sx = 0, sy = 0, n = 0;
  for (const p of lot) {
    if (!Number.isFinite(p?.[0]) || !Number.isFinite(p?.[1])) continue;
    sx += p[0]; sy += p[1]; n++;
  }
  if (!n) return { loc: BUCURESTI, sursa: "implicit" };
  const loc = laWgs84(-(sy / n + origine[1]), sx / n + origine[0]);
  if (!Number.isFinite(loc.lat) || !Number.isFinite(loc.lon) || !inRomania(loc)) {
    return { loc: BUCURESTI, sursa: "implicit" };
  }
  return { loc, sursa: "parcela" };
}

/** Ceasul locului, nu al calculatorului: un proiect din București se citește în
 *  ora Bucureștiului chiar dacă browserul stă în alt fus. */
export function acumLa(fus: string = FUS_RO):
    { an: number; luna: number; zi: number; minut: number } {
  const f = new Intl.DateTimeFormat("en-GB", {
    timeZone: fus, hour12: false, year: "numeric", month: "2-digit",
    day: "2-digit", hour: "2-digit", minute: "2-digit",
  });
  const p: Record<string, string> = {};
  for (const x of f.formatToParts(new Date())) p[x.type] = x.value;
  return { an: +p.year, luna: +p.month, zi: +p.day,
           minut: ((+p.hour) % 24) * 60 + (+p.minute) };
}

/** Punctele cardinale, pentru citirea azimutului. */
export function cardinal(az: number): string {
  const N = ["N", "NNE", "NE", "ENE", "E", "ESE", "SE", "SSE",
             "S", "SSV", "SV", "VSV", "V", "VNV", "NV", "NNV"];
  return N[Math.round(m360(az) / 22.5) % 16];
}
